<template>
  <div v-if="$route.path !== '/login'" class="app-container" :class="{ 'sidebar-collapsed': isCollapsed }">
    <div class="sidebar-wrapper">
      <div class="logo-area" style="cursor:pointer" @click="$router.push('/')">
        <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M20 5 L35 15 L35 35 L5 35 L5 15 Z' fill='%234096ff' opacity='0.8'/%3E%3Ctext x='20' y='27' text-anchor='middle' fill='white' font-size='16' font-weight='bold'%3ES%3C/text%3E%3C/svg%3E"
             class="logo-img" />
        <transition name="fade">
          <span v-show="!isCollapsed" class="logo-text">舜康ERP</span>
        </transition>
      </div>
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapsed"
        background-color="#1d2b3e"
        text-color="#b0b9c6"
        active-text-color="#409eff"
        router
        unique-opened>
        <el-menu-item index="/">
          <i class="el-icon-s-home"></i>
          <span slot="title">控制台</span>
        </el-menu-item>
        <template v-for="item in filteredMenu">
          <el-submenu v-if="item.children && item.children.length" :key="item.path" :index="item.path">
            <template slot="title">
              <i :class="item.icon || 'el-icon-document'"></i>
              <span>{{ item.name }}</span>
            </template>
            <template v-for="sub in item.children">
              <el-submenu v-if="sub.children && sub.children.length" :key="sub.path" :index="sub.path">
                <template slot="title">
                  <i :class="sub.icon || 'el-icon-document'"></i>
                  <span>{{ sub.name }}</span>
                </template>
                <el-menu-item v-for="leaf in sub.children" :key="leaf.path" :index="leaf.path">
                  <i :class="leaf.icon || 'el-icon-document'"></i>
                  <span>{{ leaf.name }}</span>
                </el-menu-item>
              </el-submenu>
              <el-menu-item v-else :key="sub.path" :index="sub.path">
                <i :class="sub.icon || 'el-icon-document'"></i>
                <span>{{ sub.name }}</span>
              </el-menu-item>
            </template>
          </el-submenu>
          <el-menu-item v-else :key="item.path" :index="item.path">
            <i :class="item.icon || 'el-icon-document'"></i>
            <span slot="title">{{ item.name }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </div>
    <!-- 手机抽屉式菜单遮罩：点击遮罩关闭抽屉 -->
    <transition name="fade">
      <div v-if="isMobile && !isCollapsed" class="mobile-menu-mask" @click="isCollapsed = true"></div>
    </transition>
    <div class="main-wrapper">
      <div class="topbar">
        <div class="topbar-left">
          <i :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" class="collapse-btn" @click="isCollapsed = !isCollapsed"></i>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item v-if="$route.meta.title">{{ $route.meta.title }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="topbar-right">
          <el-dropdown trigger="click" @command="handleCommand">
            <span class="user-info">
              <i class="el-icon-user-solid"></i>
              <span class="user-name">{{ userName }}</span>
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">个人设置</el-dropdown-item>
              <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      <div class="content-area">
        <router-view />
      </div>
    </div>
  </div>
  <router-view v-else />
</template>

<script>
import menuList from '@/config/menus'
import { canWrite, isWriteActionText, READONLY_TIP, READONLY_BYPASS_PATHS } from '@/utils/perm'

export default {
  name: 'App',
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      userName: '',
      menuPerm: null,
      menuList
    }
  },
  computed: {
    activeMenu() {
      return this.$route.path
    },
    filteredMenu() {
      // menu_perm 为 null 才是系统管理员（显示全部菜单）；空数组表示无任何菜单权限
      if (this.menuPerm === null) return this.menuList
      const menuPerm = Array.isArray(this.menuPerm) ? this.menuPerm : []
      const hasPerm = (path) => menuPerm.some(p => p === path || path.startsWith(p + '/'))
      const filterTree = (items) => {
        return items.filter(item => {
          if (hasPerm(item.path)) {
            if (item.children) item.children = filterTree(item.children)
            return true
          }
          if (item.children) {
            const filtered = filterTree(item.children)
            if (filtered.length > 0) {
              item.children = filtered
              return true
            }
          }
          return false
        })
      }
      return filterTree(this.menuList)
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadProfile()
    // 仅查看账号：开启全局写操作拦截（覆盖所有页面，含 append-to-body 的弹窗）
    if (!canWrite()) this.setupReadonlyGuard()
  },
  watch: {
    $route() {
      this.loadProfile()
      // 手机上切换页面后自动收起抽屉菜单
      if (this.isMobile) this.isCollapsed = true
      // 切换页面后重新置灰新页面里的写操作按钮
      this.$nextTick(() => this.maskWriteButtons())
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    this.teardownReadonlyGuard()
  },
  methods: {
    // ——— 仅查看账号的全局写操作拦截 ———
    // 页面数量多、写按钮位置分散（表格行内、工具栏、弹窗内），
    // 这里在 document 上统一捕获点击并置灰，避免每个页面各写一遍；
    // 已显式声明 v-can-write 的按钮同样会被这里拦截，提示保持一致。
    setupReadonlyGuard() {
      this.guardHandler = (e) => {
        if (canWrite() || this.isReadonlyBypass()) return
        const btn = e.target && e.target.closest ? e.target.closest('button, .el-button, a') : null
        if (!btn) return
        if (!isWriteActionText((btn.innerText || btn.textContent || '').trim())) return
        e.stopPropagation()
        e.preventDefault()
        this.$message.warning(READONLY_TIP)
      }
      document.addEventListener('click', this.guardHandler, true)
      this.maskObserver = new MutationObserver(() => {
        clearTimeout(this.maskTimer)
        this.maskTimer = setTimeout(() => this.maskWriteButtons(), 120)
      })
      this.maskObserver.observe(document.body, { childList: true, subtree: true })
      this.$nextTick(() => this.maskWriteButtons())
    },
    teardownReadonlyGuard() {
      if (this.guardHandler) document.removeEventListener('click', this.guardHandler, true)
      if (this.maskObserver) this.maskObserver.disconnect()
      clearTimeout(this.maskTimer)
    },
    // 个人设置页允许修改本人信息，不受只读限制
    isReadonlyBypass() {
      return READONLY_BYPASS_PATHS.includes(this.$route && this.$route.path)
    },
    // 把写操作按钮置灰（仅视觉提示，点击由 guardHandler 拦截并说明原因）
    maskWriteButtons() {
      if (canWrite() || this.isReadonlyBypass()) return
      document.querySelectorAll('.el-button').forEach(btn => {
        if (btn.classList.contains('is-readonly-mask')) return
        const text = (btn.innerText || btn.textContent || '').trim()
        if (!isWriteActionText(text)) return
        btn.classList.add('is-disabled', 'is-readonly-mask')
        btn.setAttribute('title', READONLY_TIP)
        btn.style.opacity = '0.5'
      })
    },
    loadProfile() {
      const saved = localStorage.getItem('profile')
      const token = localStorage.getItem('token')
      if (this.$route.path === '/login') return
      if (!saved || !token) {
        this.$router.replace('/login')
        return
      }
      const profile = JSON.parse(saved)
      this.userName = (profile.name || '管理员')
      // menu_perm 为 null 表示系统管理员（全部菜单）；字段缺失视为数据异常，按无权限处理
      this.menuPerm = profile.menu_perm === undefined ? [] : profile.menu_perm
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768
      if (this.isMobile) this.isCollapsed = true
    },
    handleCommand(cmd) {
      if (cmd === 'profile') {
        this.$router.push('/system/profile')
      } else if (cmd === 'logout') {
        this.$confirm('确认退出登录？', '提示', { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }).then(() => {
          localStorage.removeItem('profile')
          localStorage.removeItem('token')
          this.userName = ''
          window.location.hash = '#/login'
          window.location.reload()
        }).catch(() => {})
      }
    }
  }
}
</script>

<style>
html, body, #app { height: 100%; margin: 0; }
.app-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

/* 弹窗相对于内容区居中（左偏移侧边栏宽度） */
.el-dialog__wrapper {
  left: 240px !important;
}
.sidebar-collapsed .el-dialog__wrapper {
  left: 64px !important;
}
/* 窄屏（手机/小屏）下所有弹窗宽度铺满整个可视区，方便触屏操作 */
@media (max-width: 768px) {
  .el-dialog {
    width: 100% !important;
  }
  .el-dialog__wrapper {
    left: 0 !important;
  }
  .sidebar-collapsed .el-dialog__wrapper {
    left: 0 !important;
  }
}
.sidebar-wrapper {
  width: 240px;
  background: #1d2b3e;
  display: flex;
  flex-direction: column;
  transition: width 0.3s;
  overflow: hidden;
  flex-shrink: 0;
}
.sidebar-collapsed .sidebar-wrapper {
  width: 64px;
}
.logo-area {
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: rgba(0,0,0,0.15);
  flex-shrink: 0;
  gap: 10px;
}
.logo-img {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}
.logo-text {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
}
.sidebar-wrapper .el-menu {
  border-right: none;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.sidebar-wrapper .el-menu::-webkit-scrollbar { width: 4px; }
.sidebar-wrapper .el-menu::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 2px; }
/* 侧边菜单紧凑行距 */
.sidebar-wrapper .el-menu-item,
.sidebar-wrapper .el-submenu__title {
  height: 40px;
  line-height: 40px;
}
.sidebar-wrapper .el-menu-item i,
.sidebar-wrapper .el-submenu__title i {
  line-height: 40px;
}
.sidebar-wrapper .el-menu--inline .el-menu-item {
  height: 36px;
  line-height: 36px;
}
.sidebar-wrapper .el-menu--inline .el-menu-item i {
  line-height: 36px;
}

.main-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}
.topbar {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e6e9f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
}
.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.collapse-btn {
  font-size: 20px;
  cursor: pointer;
  color: #5a6a7e;
}
.collapse-btn:hover { color: #409eff; }
.topbar-right .user-info {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5a6a7e;
}
.topbar-right .user-info:hover { color: #409eff; }
.content-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  background: #f0f2f5;
}
.page-card {
  background: #fff;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}
.page-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* 手机抽屉式菜单遮罩：覆盖在内容区上方，点击可关闭抽屉 */
.mobile-menu-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.35);
}
/* 手机：菜单展开（抽屉）时脱离文档流悬浮覆盖，不压缩右侧内容区 */
@media (max-width: 768px) {
  .app-container:not(.sidebar-collapsed) .sidebar-wrapper {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 2001;
    width: 240px;
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.3);
  }
}
</style>
